import { alpha } from '@mui/material/styles';

export const REVIEW_ACCENT = '#4ce1b6';
export const REVIEW_ACCENT_DARK = '#3ab896';

export const REVIEW_DARK = {
  surface: '#232329',
  elevated: '#2a2a31',
  sunken: '#1c1c22',
  panel: '#333246',
  border: alpha('#ffffff', 0.08),
  borderStrong: alpha('#ffffff', 0.12),
  text: '#f5f5f5',
  textMuted: '#9ca3af',
  textSoft: '#dddddd',
};

export const accentGradient = `linear-gradient(90deg, ${REVIEW_ACCENT} 0%, ${REVIEW_ACCENT_DARK} 100%)`;
export const accentGradient135 = `linear-gradient(135deg, ${REVIEW_ACCENT} 0%, ${REVIEW_ACCENT_DARK} 100%)`;

export function getReviewPalette(isDark) {
  return {
    isDark,
    accent: REVIEW_ACCENT,
    border: isDark ? REVIEW_DARK.borderStrong : '#e0e0e0',
    borderSoft: isDark ? REVIEW_DARK.border : alpha('#000', 0.08),
    lineSubtle: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08),
    text: isDark ? REVIEW_DARK.textSoft : '#333333',
    textMuted: isDark ? REVIEW_DARK.textMuted : '#666666',
    surface: isDark ? REVIEW_DARK.elevated : '#ffffff',
    panel: isDark ? REVIEW_DARK.panel : '#f5f5f5',
    rowEven: isDark ? REVIEW_DARK.elevated : '#ffffff',
    rowOdd: isDark ? REVIEW_DARK.panel : '#fafafa',
  };
}

/** Shared section card — walk-in, reservation, and quick book (compact when flat). */
export function reviewSectionCardSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: flat ? 2 : 3,
    p: flat ? 2 : 3,
    border: `1px solid ${p.border}`,
    background: isDark
      ? `linear-gradient(145deg, ${REVIEW_DARK.panel} 0%, ${REVIEW_DARK.elevated} 52%, ${REVIEW_DARK.sunken} 100%)`
      : `linear-gradient(145deg, #ffffff 0%, ${alpha(REVIEW_ACCENT, 0.04)} 100%)`,
    boxShadow: isDark
      ? `0 1px 0 ${alpha('#fff', 0.05)} inset, 0 ${flat ? 4 : 8}px ${flat ? 16 : 24}px ${alpha('#000', 0.35)}`
      : `0 ${flat ? 2 : 4}px ${flat ? 12 : 20}px ${alpha('#0f766e', flat ? 0.06 : 0.08)}`,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: flat ? 3 : 4,
      background: accentGradient,
    },
  };
}

export function reviewPageTitleSx(isDark) {
  return {
    color: isDark ? REVIEW_DARK.textSoft : 'rgb(88, 91, 105)',
    fontWeight: 600,
    letterSpacing: '-0.02em',
  };
}

export function reviewIconBadgeSx(active, isDark, size = 48) {
  return {
    width: size,
    height: size,
    borderRadius: size >= 44 ? '50%' : 2,
    flexShrink: 0,
    background: active ? accentGradient135 : isDark ? REVIEW_DARK.panel : '#e8e8e8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.25s ease',
    boxShadow: active ? `0 4px 14px ${alpha(REVIEW_ACCENT, 0.45)}` : 'none',
    border: active ? 'none' : `1px solid ${isDark ? REVIEW_DARK.border : alpha('#000', 0.06)}`,
    '& .MuiSvgIcon-root': {
      color: active ? '#000' : isDark ? alpha('#fff', 0.35) : '#999',
      fontSize: size >= 44 ? 24 : 18,
      transition: 'color 0.25s ease',
    },
  };
}

export function reviewSwitchSx() {
  return {
    '& .MuiSwitch-switchBase.Mui-checked': { color: REVIEW_ACCENT },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: REVIEW_ACCENT,
    },
  };
}

export function reviewSliderSx() {
  return {
    color: REVIEW_ACCENT,
    '& .MuiSlider-thumb': {
      '&:hover, &.Mui-focusVisible': {
        boxShadow: `0px 0px 0px 8px ${alpha(REVIEW_ACCENT, 0.16)}`,
      },
    },
    '& .MuiSlider-valueLabel': {
      backgroundColor: REVIEW_ACCENT,
      color: '#000',
      fontWeight: 600,
    },
  };
}

export function reviewInvoiceHeaderSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    p: flat ? 2 : 3,
    borderBottom: `1px solid ${p.border}`,
    background: isDark
      ? `linear-gradient(135deg, ${REVIEW_DARK.panel} 0%, ${REVIEW_DARK.elevated} 100%)`
      : `linear-gradient(135deg, ${alpha(REVIEW_ACCENT, 0.06)} 0%, #ffffff 100%)`,
  };
}

export function reviewGuestPanelSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    p: flat ? 1.25 : 1.5,
    borderRadius: 2,
    minWidth: { xs: '100%', md: 'auto' },
    background: isDark
      ? alpha('#fff', 0.03)
      : alpha(REVIEW_ACCENT, 0.04),
    border: `1px solid ${isDark ? alpha(REVIEW_ACCENT, 0.2) : alpha(REVIEW_ACCENT, 0.25)}`,
    backdropFilter: 'blur(8px)',
  };
}

export function reviewGuestRowSx(isDark) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    justifyContent: 'space-between',
  };
}

export function reviewTableShellSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    border: `1px solid ${flat ? p.lineSubtle : p.border}`,
    borderRadius: flat ? 1.5 : 2,
    overflow: 'hidden',
    mb: flat ? 2 : 3,
    boxShadow: isDark && !flat ? `inset 0 1px 0 ${alpha('#fff', 0.04)}` : 'none',
  };
}

export function reviewTableHeaderSx(isDark) {
  const p = getReviewPalette(isDark);
  return {
    display: 'flex',
    background: isDark
      ? `linear-gradient(90deg, ${REVIEW_DARK.panel} 0%, ${REVIEW_DARK.elevated} 100%)`
      : `linear-gradient(90deg, #f5f5f5 0%, ${alpha(REVIEW_ACCENT, 0.08)} 100%)`,
    borderBottom: `2px solid ${isDark ? alpha(REVIEW_ACCENT, 0.25) : alpha(REVIEW_ACCENT, 0.35)}`,
    py: 1.5,
    px: 2,
  };
}

export function reviewTableRowSx(isDark, flat, index, total) {
  const p = getReviewPalette(isDark);
  const isEven = index % 2 === 0;
  return {
    display: 'flex',
    borderBottom: index < total - 1 ? `1px solid ${p.borderSoft}` : 'none',
    py: 2,
    px: 2,
    backgroundColor: isEven ? p.rowEven : p.rowOdd,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: isDark ? alpha(REVIEW_ACCENT, 0.06) : alpha(REVIEW_ACCENT, 0.05),
    },
  };
}

export function reviewTotalsPanelSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    position: 'relative',
    overflow: 'hidden',
    background: isDark
      ? `linear-gradient(165deg, ${alpha(REVIEW_ACCENT, 0.1)} 0%, ${alpha('#000', 0.22)} 42%, ${REVIEW_DARK.elevated} 100%)`
      : `linear-gradient(165deg, ${alpha(REVIEW_ACCENT, 0.1)} 0%, #ffffff 45%, ${alpha('#f8fafc', 0.95)} 100%)`,
    p: flat ? 1.5 : 2,
    borderRadius: 2.5,
    border: `1px solid ${isDark ? alpha(REVIEW_ACCENT, 0.28) : alpha(REVIEW_ACCENT, 0.28)}`,
    boxShadow: isDark
      ? `0 8px 22px ${alpha('#000', 0.28)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`
      : `0 8px 20px ${alpha('#0f766e', 0.08)}, inset 0 1px 0 ${alpha('#fff', 0.8)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: 3,
      bottom: 0,
      background: accentGradient,
    },
  };
}

export function reviewTotalHighlightSx(isDark, flat = false) {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 2,
    p: flat ? 1.25 : 1.5,
    background: accentGradient135,
    boxShadow: `0 6px 18px ${alpha(REVIEW_ACCENT, isDark ? 0.35 : 0.28)}`,
  };
}

export function reviewTotalsRowSx(isDark, { accent = false, muted = false } = {}) {
  const p = getReviewPalette(isDark);
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1.5,
    py: 0.65,
    borderBottom: `1px dashed ${p.lineSubtle}`,
    '&:last-of-type': { borderBottom: 'none' },
    '& .review-totals-label': {
      color: muted ? p.textMuted : p.textMuted,
      fontSize: '0.78rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    '& .review-totals-value': {
      color: accent ? (isDark ? '#ffcc80' : '#e65100') : p.text,
      fontWeight: 700,
      fontSize: '0.875rem',
      fontVariantNumeric: 'tabular-nums',
    },
  };
}

export function reviewExtraChargesLinkSx(isDark) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.78rem',
    fontWeight: 700,
    color: isDark ? REVIEW_ACCENT : '#0f766e',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    p: 0,
    letterSpacing: '0.01em',
  };
}

export function reviewInvoiceHeaderCompactSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    px: flat ? 1.5 : 2,
    py: flat ? 1.25 : 1.5,
    borderBottom: `1px solid ${p.border}`,
    background: isDark
      ? `linear-gradient(120deg, ${alpha(REVIEW_ACCENT, 0.12)} 0%, ${REVIEW_DARK.elevated} 55%, ${REVIEW_DARK.panel} 100%)`
      : `linear-gradient(120deg, ${alpha(REVIEW_ACCENT, 0.14)} 0%, #ffffff 55%, ${alpha('#f8fafc', 0.9)} 100%)`,
  };
}

export function reviewGuestChipSx(isDark) {
  return {
    height: 28,
    maxWidth: '100%',
    fontWeight: 600,
    fontSize: '0.72rem',
    backgroundColor: isDark ? alpha('#fff', 0.06) : alpha(REVIEW_ACCENT, 0.1),
    color: isDark ? REVIEW_DARK.textSoft : '#134e4a',
    border: `1px solid ${isDark ? alpha(REVIEW_ACCENT, 0.28) : alpha(REVIEW_ACCENT, 0.3)}`,
    '& .MuiChip-icon': {
      color: isDark ? REVIEW_ACCENT : '#0f766e',
      ml: '8px',
      fontSize: 16,
    },
    '& .MuiChip-label': {
      px: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };
}

export function reviewCompactToggleGroupSx(isDark, borderColor) {
  return {
    ...reviewToggleGroupSx(isDark, borderColor),
    '& .MuiToggleButton-root': {
      ...reviewToggleGroupSx(isDark, borderColor)['& .MuiToggleButton-root'],
      py: 1,
      fontSize: '0.8rem',
      borderRadius: '10px !important',
      mx: 0.25,
      borderWidth: 1,
    },
  };
}

export function reviewPaymentMethodChipSx(isDark, selected, warn = false) {
  return {
    backgroundColor: selected
      ? REVIEW_ACCENT
      : isDark
        ? alpha('#fff', 0.05)
        : alpha(REVIEW_ACCENT, 0.06),
    color: selected ? '#000' : isDark ? REVIEW_DARK.textSoft : '#334155',
    border: `1.5px solid ${
      selected
        ? REVIEW_ACCENT
        : warn
          ? '#ef5350'
          : isDark
            ? alpha(REVIEW_ACCENT, 0.22)
            : alpha(REVIEW_ACCENT, 0.22)
    }`,
    fontWeight: selected ? 700 : 560,
    fontSize: '0.75rem',
    height: 30,
    px: 0.5,
    cursor: 'pointer',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
    boxShadow: selected ? `0 4px 12px ${alpha(REVIEW_ACCENT, 0.35)}` : 'none',
    '&:hover': {
      backgroundColor: selected
        ? REVIEW_ACCENT_DARK
        : isDark
          ? alpha(REVIEW_ACCENT, 0.12)
          : alpha(REVIEW_ACCENT, 0.12),
      borderColor: REVIEW_ACCENT,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 10px ${alpha(REVIEW_ACCENT, 0.2)}`,
    },
  };
}

export function reviewPaymentPanelSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    p: flat ? 1.25 : 1.5,
    borderRadius: 2,
    border: `1px solid ${p.borderSoft}`,
    background: isDark
      ? alpha('#fff', 0.025)
      : alpha(REVIEW_ACCENT, 0.03),
  };
}

export function reviewMealPlanChipSx(isDark) {
  return {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 600,
    backgroundColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
    color: isDark ? REVIEW_DARK.textSoft : '#555',
  };
}

export function reviewRoomNumberChipSx() {
  return {
    height: 22,
    fontSize: '0.7rem',
    backgroundColor: alpha(REVIEW_ACCENT, 0.12),
    color: REVIEW_ACCENT,
    fontWeight: 600,
    border: `1px solid ${alpha(REVIEW_ACCENT, 0.4)}`,
  };
}

export function reviewToggleGroupSx(isDark, borderColor) {
  return {
    width: '100%',
    '& .MuiToggleButton-root': {
      borderColor,
      color: isDark ? REVIEW_DARK.textSoft : 'rgb(88, 91, 105)',
      flex: 1,
      py: 1.5,
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      transition: 'all 0.25s ease',
      '&.Mui-selected': {
        background: accentGradient135,
        color: '#000',
        borderColor: REVIEW_ACCENT,
        fontWeight: 600,
        boxShadow: `0 4px 12px ${alpha(REVIEW_ACCENT, 0.35)}`,
        '&:hover': {
          background: `linear-gradient(135deg, ${REVIEW_ACCENT_DARK} 0%, ${REVIEW_ACCENT} 100%)`,
          borderColor: REVIEW_ACCENT_DARK,
        },
      },
      '&:hover': {
        backgroundColor: isDark ? REVIEW_DARK.panel : '#f5f5f5',
        borderColor: REVIEW_ACCENT,
      },
    },
  };
}

export function reviewSectionHeadingSx(isDark, flat = false) {
  return {
    color: isDark ? REVIEW_DARK.textSoft : 'rgb(88, 91, 105)',
    fontWeight: 600,
    fontSize: flat ? '1rem' : '1.125rem',
  };
}

export function reviewMobileRoomCardSx(isDark, flat, index, total) {
  const p = getReviewPalette(isDark);
  const isEven = index % 2 === 0;
  return {
    p: flat ? 1.25 : 1.5,
    borderBottom: index < total - 1 ? `1px solid ${p.borderSoft}` : 'none',
    backgroundColor: isEven ? p.rowEven : p.rowOdd,
  };
}

export function reviewMobileDetailRowSx() {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1.5,
    py: 0.5,
  };
}

export function reviewMobileDetailLabelSx(isDark) {
  return {
    color: isDark ? REVIEW_DARK.textMuted : '#666',
    fontSize: '0.75rem',
    fontWeight: 600,
    flexShrink: 0,
    minWidth: 88,
  };
}

export function reviewMobileDetailValueSx(isDark, { accent = false, bold = false } = {}) {
  return {
    color: accent ? REVIEW_ACCENT : isDark ? REVIEW_DARK.textSoft : '#333',
    fontSize: accent || bold ? '0.875rem' : '0.8125rem',
    fontWeight: bold || accent ? 700 : 500,
    textAlign: 'right',
    flex: 1,
    wordBreak: 'break-word',
  };
}

export function reviewMobileAmountRowSx(isDark) {
  const p = getReviewPalette(isDark);
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: 1,
    pt: 1,
    borderTop: `1px solid ${p.borderSoft}`,
  };
}

export function reviewTaxModePanelSx(isDark, flat = false) {
  const p = getReviewPalette(isDark);
  return {
    mt: flat ? 1.5 : 2,
    p: flat ? 1.5 : 2,
    borderRadius: 2,
    border: `1px solid ${alpha(REVIEW_ACCENT, isDark ? 0.28 : 0.35)}`,
    background: isDark
      ? `linear-gradient(160deg, ${alpha(REVIEW_ACCENT, 0.1)} 0%, ${REVIEW_DARK.panel} 55%, ${REVIEW_DARK.elevated} 100%)`
      : `linear-gradient(160deg, ${alpha(REVIEW_ACCENT, 0.08)} 0%, #ffffff 60%, ${alpha(REVIEW_ACCENT, 0.04)} 100%)`,
    boxShadow: isDark
      ? `0 0 0 1px ${alpha(REVIEW_ACCENT, 0.08)} inset`
      : `0 4px 16px ${alpha(REVIEW_ACCENT, 0.1)}`,
  };
}

export function reviewTaxModeToggleSx(isDark, compact = false) {
  return {
    '& .MuiToggleButton-root': {
      flex: compact ? '0 0 auto' : 1,
      py: compact ? 0.35 : 1.1,
      px: compact ? 1.25 : 1.25,
      minWidth: compact ? 72 : undefined,
      fontSize: compact ? '0.75rem' : undefined,
      fontWeight: 600,
      textTransform: 'none',
      borderColor: isDark ? alpha('#fff', 0.12) : alpha(REVIEW_ACCENT, 0.25),
      '&.Mui-selected': {
        background: accentGradient135,
        color: '#000',
        fontWeight: 700,
        boxShadow: compact ? 'none' : `0 4px 14px ${alpha(REVIEW_ACCENT, 0.35)}`,
        '&:hover': {
          background: accentGradient135,
        },
      },
    },
  };
}

export function reviewTaxModeChipSx(isDark, accent = false) {
  return {
    height: 24,
    fontSize: '0.7rem',
    fontWeight: accent ? 600 : 500,
    backgroundColor: accent
      ? alpha(REVIEW_ACCENT, isDark ? 0.22 : 0.16)
      : isDark
        ? alpha('#fff', 0.06)
        : alpha('#000', 0.04),
    color: isDark ? REVIEW_DARK.textSoft : '#333',
    border: `1px solid ${alpha(REVIEW_ACCENT, accent ? 0.45 : 0.2)}`,
  };
}
